import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { cableId, type CableId } from './Cable.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.sensor_calibration_ */
export type SensorCalibrationId = string & { __brand: 'SensorCalibrationId' }

/** Represents the table public.sensor_calibration_ */
export default interface SensorCalibrationTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    SensorCalibrationId,
    SensorCalibrationId | undefined,
    SensorCalibrationId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  sensorId: ColumnType<string, string, string>

  /** Database type: pg_catalog.int4 */
  cableId: ColumnType<CableId, CableId, CableId>

  /** Database type: pg_catalog.int4 */
  calibrationId: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  sensorNumber: ColumnType<number, number, number>

  /** Database type: pg_catalog.numeric */
  calibrationOffset: ColumnType<number | null, number | null, number | null>
}

export type SensorCalibration = Selectable<SensorCalibrationTable>

export type SensorCalibrationInitializer = Insertable<SensorCalibrationTable>

export type SensorCalibrationMutator = Updateable<SensorCalibrationTable>

export const sensorCalibrationId =
  z.string() as unknown as z.Schema<SensorCalibrationId>

export const sensorCalibration = z.object({
  id: sensorCalibrationId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  sensorId: z.string(),
  cableId: cableId,
  calibrationId: z.number().nullable().optional(),
  sensorNumber: z.number(),
  calibrationOffset: z.number().nullable().optional(),
}) as unknown as z.Schema<SensorCalibration>

export const sensorCalibrationInitializer = z.object({
  id: sensorCalibrationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  sensorId: z.string(),
  cableId: cableId,
  calibrationId: z.number().optional().nullable(),
  sensorNumber: z.number(),
  calibrationOffset: z.number().optional().nullable(),
}) as unknown as z.Schema<SensorCalibrationInitializer>

export const sensorCalibrationMutator = z.object({
  id: sensorCalibrationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  sensorId: z.string().optional(),
  cableId: cableId.optional(),
  calibrationId: z.number().optional().nullable(),
  sensorNumber: z.number().optional(),
  calibrationOffset: z.number().optional().nullable(),
}) as unknown as z.Schema<SensorCalibrationMutator>
